/**
 * Per-tick GOALKEEPER TRAVEL — the ground step a keeper's position-first mover
 * applied this tick, so the thing that picks his clip can tell whether he is
 * actually moving. (§4.5)
 *
 * ── The defect this exists to fix ───────────────────────────────────────────
 *
 * Owner report, verbatim: *"make sure no player ever teleport or gets slided to
 * reach his posi"*. The keeper is the one player in the game who is genuinely
 * slid, and he is slid 100% — measured, not estimated.
 *
 * A keeper is not moved by locomotion. Two movers write his position directly
 * and then ZERO his velocity so that step.ts stage 4 cannot integrate it and
 * move him a second time:
 *
 *   - `goalkeeper.ts moveToward` — every reposition, shuffle and sweep, in open
 *     play, at up to MAX_SPEED_SPRINT × his pace.
 *   - `rules/fsm.ts applyStageTargets` — the set-piece walk-in's GK carve-out.
 *
 * Zeroed velocity is a reasonable answer to double-integration and a disastrous
 * one to animation, because |v| is what every clip chooser reads:
 * `player-controller.ts stepAnim` derives the locomotion clip from |v| alone, so
 * v = 0 ⇒ Idle, and `goalkeeper.ts setStanceAnim` then stamps `AnimState.GkIdle`
 * over the top at stage 5 regardless. `GkIdle` binds to the SUSTAINED POSE
 * `GkReady` in the renderer (`playerAnimator.ts` `sustainedPoseClip`), held on
 * the base layer at FULL weight with the gait underneath at weight ZERO.
 *
 * A pose means a planted foot. So every metre a keeper covers is a metre of
 * pure skating — there is no cycle playing to carry any of it.
 *
 * MEASURED (`locomotion-honesty.test.ts`, 180 s of real 11v11, seed 20260809):
 * keeper |Δp|/dt − |v| is p50 0.3159 / p90 3.5017 / p99 8.5017 m/s against an
 * outfield p90 of 0.0015; 97% of every drag sample in the match is a keeper;
 * a keeper travels >1.4 m/s for 3.2% of his time and **99.0% of that is stamped
 * GkIdle**, i.e. 45.7 m slid under a planted foot in three minutes.
 *
 * ── Why record the step instead of making the velocity honest ───────────────
 *
 * Honest velocity is the better long-term answer and is what `applyStageTargets`
 * did for outfielders. It is NOT what this does, deliberately:
 *
 *   1. The keeper runs at stage 5, AFTER stage 4's integration, so a velocity
 *      written here is integrated on the NEXT tick — the double-move
 *      `moveToward`'s header documents (it measured a keeper repositioning at a
 *      superhuman ~17 m/s). Preventing that needs a lock that spans a tick
 *      boundary, which is a different and larger change.
 *   2. `resolveSave` reads the keeper's speed as STRETCH. `moveToward`'s header
 *      records that zero speed is what lets a set keeper GATHER cleanly instead
 *      of spilling. Making velocity honest changes save outcomes, and this is an
 *      animation defect, not a physics one.
 *
 * So this changes `p.anim` and nothing else. Positions and velocities are
 * byte-identical to before, no save can flip, and no match outcome can move.
 *
 * ── Why a module-scope buffer and not a WorldState field ────────────────────
 *
 * Identical argument to `rules/stage-lock.ts`, which this mirrors deliberately:
 * it is a per-tick CONTROL HINT flowing one way between two stages of the SAME
 * tick, written by whichever mover ran and read by the clip chooser, and never
 * read across ticks. It is therefore never hashed, cloned or serialized and
 * cannot desync (§15.3, D4). Putting it in `PlayerArrays` would mean a hashed
 * field and a wire decision for information that is derivable and dead by the
 * end of the tick.
 *
 * The tick stamp is load-bearing for the same reason it is there: module scope
 * is shared by every WorldState in the process (the client runs a predicted
 * world alongside re-simulations, and the tests build many worlds), so a reader
 * only believes the buffer if it was stamped with the tick being asked about.
 * A keeper stepped without the buffer having been begun therefore reads "no
 * travel" and behaves exactly as he did before this existed — fail-safe.
 */

import type { WorldState } from './types.js';
import { MAX_PLAYERS } from './types.js';

/** Ground step applied to this slot by a position-first mover this tick, metres. */
const stepX = new Float64Array(MAX_PLAYERS);
const stepZ = new Float64Array(MAX_PLAYERS);

/**
 * The tick the buffers describe. -1 = "nothing has stamped it", which no real
 * tick can equal (step.ts increments from 0), so a stale buffer is never
 * believed.
 */
let stampedTick = -1;

/**
 * Drop last tick's steps and stamp the buffers for THIS tick.
 *
 * Called once per tick, UNCONDITIONALLY, from step.ts — before stage 2, because
 * `applyStageTargets`'s GK carve-out is one of the two writers and it runs
 * there. A world that moves no keeper always clears the buffer before anything
 * can read it, so one world's corner can never colour another world's keeper.
 */
export function beginGkTravelTick(state: WorldState): void {
  stepX.fill(0);
  stepZ.fill(0);
  stampedTick = state.tick;
}

/**
 * Record the ground step a position-first mover just applied to slot `i`.
 *
 * Additive, because a keeper can legitimately be moved twice in one tick by two
 * different movers (the walk-in at stage 2 and his own sweep at stage 5), and
 * what the clip chooser needs is the TOTAL he travelled, not the last leg.
 */
export function recordGkTravel(i: number, dx: number, dz: number): void {
  stepX[i]! += dx;
  stepZ[i]! += dz;
}

/** Ground distance slot `i` was moved by a position-first mover on `state.tick`. */
export function gkTravelDistance(state: WorldState, i: number): number {
  if (stampedTick !== state.tick) return 0;
  const dx = stepX[i]!;
  const dz = stepZ[i]!;
  return Math.sqrt(dx * dx + dz * dz);
}

/** The X component of that step. Meaningless unless `gkTravelDistance` > 0. */
export function gkTravelX(state: WorldState, i: number): number {
  return stampedTick === state.tick ? stepX[i]! : 0;
}

/** The Z component of that step. Meaningless unless `gkTravelDistance` > 0. */
export function gkTravelZ(state: WorldState, i: number): number {
  return stampedTick === state.tick ? stepZ[i]! : 0;
}
